import axios, { AxiosError } from "axios"
import {
  clearStoredToken,
  getStoredToken,
} from "../localstorage"
import { getApiUrlFromDomain } from "@/constants/domains"

/**
 * Determines which API URL to use based on the current domain
 * @returns API base URL
 */
const getApiBaseUrl = (): string => {
  // Check if running on client side
  if (typeof window === "undefined") {
    return import.meta.env.VITE_HOST_API_URL ?? ""
  }

  const currentDomain = window.location.host
  return getApiUrlFromDomain(currentDomain)
}

export const axiosApi = axios.create({
  baseURL: getApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
})

axiosApi.interceptors.request.use(
  (config) => {
    const authData = getStoredToken()
    if (authData) {
      config.headers.Authorization = `Bearer ${authData.accessToken}`
    }
    return config
  },
  (error) => {
    Promise.reject(error)
  }
)

const handleUnauthorizedError = async (
  error: AxiosError<ApiError>
) => {
  clearStoredToken()
  window.location.href = "/"
  return Promise.reject(error)
}

const createGenericError = (status?: number): ApiError => ({
  name: "UnknownError",
  propagable: false,
  responseCode: status || 500,
  message: "An unknown error occurred.",
})

axiosApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    if (!error.response) {
      return Promise.reject(error)
    }

    const { status, data } = error.response

    if (status === 401) {
      return handleUnauthorizedError(error)
    }

    if (data) {
      return Promise.reject(data)
    }

    return Promise.reject(createGenericError(status))
  }
)
